import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import PhotoLightbox from '../components/PhotoLightbox';
import Plate from '../components/Plate';
import { sinistresAPI, rapportsAPI, BASE_URL } from '../services/api';
import { ArrowLeft, Pencil, FileText, Plus, Car, User, Shield, MapPin, ExternalLink, Download, Image as ImageIcon, ZoomIn, Clock, History } from 'lucide-react';
import toast from 'react-hot-toast';

// Local Plate component removed in favor of shared component

const SinistreDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [sinistre, setSinistre] = useState(null);
    const [loading, setLoading] = useState(true);

    // Lightbox state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    useEffect(() => {
        setLoading(true);
        sinistresAPI.getById(id)
            .then(res => setSinistre(res.data.data))
            .catch(() => toast.error('Dossier non trouvé'))
            .finally(() => setLoading(false));
    }, [id]);

    const handleDownloadPDF = async (reportId, numRapport) => {
        try {
            toast.loading('Génération du PDF...', { id: 'pdf' });
            const res = await rapportsAPI.generatePDF(reportId);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Rapport_${numRapport}.pdf`);
            document.body.appendChild(link);
            link.click();
            toast.success('PDF téléchargé', { id: 'pdf' });
        } catch {
            toast.error('Erreur lors du téléchargement', { id: 'pdf' });
        }
    };

    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    // Build a normalized photos array to handle different photo formats
    const getPhotoUrl = (photo) => {
        if (!photo) return '';
        // If it's a full URL (legacy Cloudinary), return as is
        if (photo.file_path && photo.file_path.startsWith('http')) {
            return photo.file_path;
        }
        // If it's a relative path (new local storage), prepend BASE_URL
        const path = photo.file_path || `/uploads/photos/${photo.filename}`;
        // Ensure path starts with /
        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        return `${BASE_URL}${cleanPath}`;
    };

    if (loading) return <Layout title="Chargement..."><div className="loading-spinner"><div className="spinner" /></div></Layout>;
    if (!sinistre) return <Layout title="Erreur"><div className="empty-state"><h3>Sinistré introuvable</h3><button className="btn btn-primary" onClick={() => navigate('/sinistres')}>Retour</button></div></Layout>;

    const photos = sinistre.photos || [];

    return (
        <Layout title={`Sinistré ${sinistre.reference_interne || sinistre.numero_sinistre}`}>
            {/* Lightbox */}
            {lightboxOpen && photos.length > 0 && (
                <PhotoLightbox
                    photos={photos}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxOpen(false)}
                />
            )}

            <div style={{ maxWidth: '1100px' }}>
                {/* Header Actions */}
                <div className="flex-between mb-6">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button className="btn btn-ghost" onClick={() => navigate('/sinistres')}><ArrowLeft size={18} /></button>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <h2 style={{ fontSize: '22px', fontWeight: '800', color: 'var(--primary-800)', margin: 0, display: 'flex', alignItems: 'center' }}>
                                    {sinistre.reference_interne
                                        ? <><span style={{ letterSpacing: '1px', fontFamily: 'monospace' }}>{sinistre.reference_interne}</span><span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)', marginLeft: '10px' }}>Réf. Sinistre: {sinistre.numero_sinistre || '—'}</span></>
                                        : sinistre.numero_sinistre
                                    }
                                </h2>
                            </div>

                            <p style={{ margin: 0, marginTop: '5px' }}>
                                {sinistre.source_table === 'siniestrados' && <span className="badge badge-gold" style={{ fontSize: '10px' }}>HISTORIQUE</span>}
                                <span className={`badge ${sinistre.statut === 'brouillon' ? 'badge-orange' :
                                    sinistre.statut === 'en_cours' ? 'badge-blue' :
                                        sinistre.statut === 'termine' ? 'badge-green' : 'badge-gray'
                                    }`} style={{ marginLeft: sinistre.source_table === 'siniestrados' ? '10px' : '0' }}>
                                    {sinistre.statut === 'brouillon' ? '📝 Brouillon' :
                                        sinistre.statut === 'en_cours' ? '⌛ En cours' :
                                            sinistre.statut === 'termine' ? '✅ Terminé' : sinistre.statut || '—'}
                                </span>
                                <span style={{ fontSize: '13px', color: 'var(--text-muted)', marginLeft: '15px' }}>
                                    Accident du <b>{sinistre.date_sinistre ? new Date(sinistre.date_sinistre).toLocaleDateString('fr-DZ') : '—'}</b>
                                </span>
                            </p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-outline" onClick={() => navigate(`/sinistres/${sinistre.id}/modifier`)}>
                            <Pencil size={15} /> Modifier le Sinistré
                        </button>
                        {(!sinistre.rapports || sinistre.rapports.length === 0) && (
                            <button className="btn btn-primary" onClick={() => navigate(`/rapports/nouveau?sinistre=${sinistre.id}&num=${sinistre.numero_sinistre}&assure=${sinistre.nom_assure}`)}>
                                <FileText size={15} /> Nouveau Rapport / PV
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid-2" style={{ gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
                    {/* Colonne Gauche */}
                    <div>
                        {/* Infos assuré */}
                        <div className="card mb-6">
                            <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <User size={16} /> Informations de l'Assuré &amp; Sinistré
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '30px', alignItems: 'start' }}>
                                <div>
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>Assuré</label>
                                            {sinistre.autres_dossiers_count > 0 && (
                                                <span className="badge badge-primary" style={{ fontSize: '10px', borderRadius: '12px', padding: '2px 8px' }}>
                                                    {sinistre.autres_dossiers_count} autre(s) sinistré(s)
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--primary-900)' }}>
                                            {sinistre.nom_assure}
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>Assurance</label>
                                        <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--primary-700)', marginTop: '4px' }}>
                                            <Shield size={14} style={{ display: 'inline', marginRight: '6px' }} />
                                            {sinistre.nom_assurance || '—'}
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}><b>Police:</b> {sinistre.numero_police || '—'}</div>
                                        {sinistre.code_assurance && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}><b>Code:</b> {sinistre.code_assurance} ({sinistre.ville_assurance || ''})</div>}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>Mission &amp; Lieux</label>
                                        <div style={{ fontSize: '13px', color: 'var(--text-primary)', marginTop: '4px' }}>
                                            <MapPin size={12} style={{ display: 'inline', marginRight: '4px' }} />
                                            <b>Lieu de Visite:</b> {sinistre.lieu_visite || '—'}
                                        </div>
                                    </div>
                                </div>
                                {/* Grande visualización del Logo */}
                                {sinistre.assurance_logo && (
                                    <img
                                        src={`${BASE_URL}/assurances_logo/${sinistre.assurance_logo}`}
                                        alt="Logo Assurance"
                                        style={{ height: '90px', maxWidth: '200px', objectFit: 'contain', marginRight: '60px' }}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Véhicule */}
                        <div className="card mb-6">
                            <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Car size={16} /> Caractéristiques Techniques du Véhicule
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                                <div style={{ position: 'relative' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                        <div>
                                            <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--primary-700)' }}>{sinistre.marque_vehicule}</div>
                                            <div style={{ fontSize: '14px', fontWeight: '600' }}>{sinistre.modele_vehicule}</div>
                                        </div>
                                        {sinistre.marque_vehicule && (
                                            <img
                                                src={`${BASE_URL}/car_logos/${sinistre.marque_logo ? sinistre.marque_logo : ((sinistre.marque_vehicule || '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') + '.png')}`}
                                                alt={sinistre.marque_vehicule}
                                                style={{ height: '110px', maxWidth: '140px', objectFit: 'contain' }}
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        )}
                                    </div>
                                    <Plate value={sinistre.immatriculation} style={{ marginTop: '10px' }} />
                                </div>
                                <div style={{ fontSize: '12px', borderLeft: '1px solid var(--border)', paddingLeft: '15px' }}>
                                    <div><b>N° Série:</b> <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{sinistre.numero_serie || '—'}</span></div>
                                    <div style={{ marginTop: '4px' }}><b>Type:</b> {sinistre.type_vehicule || '—'}</div>
                                    <div style={{ marginTop: '4px' }}><b>Genre:</b> {sinistre.genre || '—'}</div>
                                    <div style={{ marginTop: '4px' }}><b>Année:</b> {sinistre.annee_vehicule || '—'}</div>
                                </div>
                                <div style={{ fontSize: '12px', borderLeft: '1px solid var(--border)', paddingLeft: '15px' }}>
                                    <div><b>Énergie:</b> {sinistre.energie || '—'}</div>
                                    <div style={{ marginTop: '4px' }}><b>Puissance:</b> {sinistre.puissance || '—'}</div>
                                    <div style={{ marginTop: '4px' }}><b>Carrosserie:</b> {sinistre.carrosserie || '—'}</div>
                                    <div style={{ marginTop: '4px' }}><b>Couleur:</b> {sinistre.couleur || '—'}</div>
                                    <div style={{ marginTop: '8px' }}>
                                        <b style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '2px' }}>État du Véhicule:</b>
                                        <span style={{ background: 'var(--primary-50)', color: 'var(--primary-700)', padding: '2px 10px', borderRadius: '12px', fontWeight: '800', fontSize: '11px', textTransform: 'uppercase', border: '1px solid var(--primary-100)' }}>
                                            {sinistre.etat_vehicule || '—'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Rapports PV */}
                        <div className="card">
                            <div className="flex-between mb-4">
                                <div className="form-section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FileText size={16} /> Procès-Verbal d'Expertise
                                </div>
                                {(!sinistre.rapports || sinistre.rapports.length === 0) && (
                                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--primary-600)' }} onClick={() => navigate(`/rapports/nouveau?sinistre=${sinistre.id}`)}>
                                        <Plus size={14} /> Créer le PV
                                    </button>
                                )}
                            </div>

                            {sinistre.rapports && sinistre.rapports.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {sinistre.rapports.map(r => (
                                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--surface-3)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                                            <div>
                                                <div style={{ fontWeight: '700', color: 'var(--primary-800)' }}>PV N° {r.numero_rapport}</div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                                    Établi le {r.date_rapport ? new Date(r.date_rapport).toLocaleDateString('fr-DZ') : '—'} • {(r.total_general || 0).toLocaleString('fr-DZ')} DA
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <Link to={`/rapports/${r.id}`} className="btn btn-ghost btn-sm" title="Consulter">
                                                    <ExternalLink size={14} />
                                                </Link>
                                                <button className="btn btn-pdf btn-sm" title="Télécharger PDF" onClick={() => handleDownloadPDF(r.id, r.numero_rapport)}>
                                                    <Download size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '30px', background: 'var(--surface-2)', borderRadius: 'var(--radius)', border: '2px dashed var(--border)' }}>
                                    <FileText size={32} color="var(--text-muted)" style={{ margin: '0 auto 10px', display: 'block' }} />
                                    <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Aucun rapport d'expertise n'a encore été créé pour ce dossier.</p>
                                    <button className="btn btn-primary btn-sm" style={{ marginTop: '12px' }} onClick={() => navigate(`/rapports/nouveau?sinistre=${sinistre.id}`)}>
                                        <Plus size={14} /> Créer le PV
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Colonne Droite */}
                    <div>
                        {/* Tiers impliqué */}
                        <div className="card mb-6">
                            <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Shield size={16} /> Tiers Impliqué
                            </div>
                            {sinistre.nom_tiers ? (
                                <div style={{ fontSize: '13px' }}>
                                    <div style={{ fontWeight: '700' }}>{sinistre.nom_tiers}</div>
                                </div>
                            ) : (
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '10px' }}>Aucun tiers renseigné</p>
                            )}
                        </div>

                        {/* Galerie Photos avec Lightbox */}
                        <div className="card mb-6">
                            <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <ImageIcon size={16} /> Photos du véhicule ({photos.length})
                                </span>
                                {photos.length > 0 && (
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => openLightbox(0)}
                                        style={{ fontSize: '11px', color: 'var(--primary-600)' }}
                                    >
                                        <ZoomIn size={13} /> Voir tout
                                    </button>
                                )}
                            </div>

                            {photos.length > 0 ? (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                        {photos.slice(0, 4).map((p, idx) => (
                                            <div
                                                key={p.id || idx}
                                                onClick={() => openLightbox(idx)}
                                                style={{
                                                    height: '90px', borderRadius: '8px', overflow: 'hidden',
                                                    cursor: 'pointer', position: 'relative',
                                                    border: '2px solid transparent',
                                                    transition: 'all 0.2s',
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.borderColor = 'var(--primary-400)';
                                                    e.currentTarget.style.transform = 'scale(1.02)';
                                                    e.currentTarget.querySelector('.zoom-overlay').style.opacity = '1';
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.borderColor = 'transparent';
                                                    e.currentTarget.style.transform = 'scale(1)';
                                                    e.currentTarget.querySelector('.zoom-overlay').style.opacity = '0';
                                                }}
                                            >
                                                <img
                                                    src={getPhotoUrl(p)}
                                                    alt={`${idx + 1}`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    loading="lazy"
                                                />
                                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: 'white', fontSize: '10px', padding: '4px', textAlign: 'center' }}>
                                                    Photo {idx + 1}
                                                </div>
                                                {/* Hover overlay */}
                                                <div
                                                    className="zoom-overlay"
                                                    style={{
                                                        position: 'absolute', inset: 0,
                                                        background: 'rgba(26,58,92,0.5)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        opacity: 0, transition: 'opacity 0.2s',
                                                    }}
                                                >
                                                    <ZoomIn size={24} color="white" />
                                                </div>
                                                {/* Index badge */}
                                                {idx === 3 && photos.length > 4 && (
                                                    <div style={{
                                                        position: 'absolute', inset: 0,
                                                        background: 'rgba(0,0,0,0.65)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: 'white', fontSize: '18px', fontWeight: '800',
                                                    }}>
                                                        +{photos.length - 3}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    {photos.length > 4 && (
                                        <button
                                            className="btn btn-ghost btn-sm"
                                            style={{ width: '100%', marginTop: '8px', fontSize: '12px' }}
                                            onClick={() => openLightbox(4)}
                                        >
                                            Voir toutes les {photos.length} photos
                                        </button>
                                    )}
                                </>
                            ) : (
                                <p style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>Aucune photo</p>
                            )}
                        </div>

                        {/* ─── Historique du Dossier ─── */}
                        <div className="card">
                            <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <History size={16} /> Historique du Dossier
                            </div>

                            {sinistre.source_table === 'siniestrados' ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', fontSize: '12px', color: 'var(--text-muted)' }}>
                                    <Clock size={14} />
                                    Dossier historique — informations de suivi non disponibles
                                </div>
                            ) : (
                                <>
                                    {/* Créé le */}
                                    <div style={{ padding: '10px 14px', background: 'var(--primary-50)', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid var(--primary-400)', marginBottom: '8px' }}>
                                        <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.5px', marginBottom: '3px' }}>
                                            📅 Créé le
                                        </div>
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--primary-700)' }}>
                                            {sinistre.created_at
                                                ? new Date(sinistre.created_at).toLocaleDateString('fr-DZ', { day: '2-digit', month: 'long', year: 'numeric' })
                                                : '—'}
                                        </div>
                                        {sinistre.createur && (
                                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <User size={11} /> par <strong>{sinistre.createur}</strong>
                                            </div>
                                        )}
                                    </div>

                                    {/* Dernière modification */}
                                    {sinistre.updated_at && (
                                        <div style={{ padding: '10px 14px', background: '#fefce8', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid var(--gold-400)', marginBottom: '12px' }}>
                                            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.5px', marginBottom: '3px' }}>
                                                ✏️ Dernière modification
                                            </div>
                                            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--gold-500)' }}>
                                                {new Date(sinistre.updated_at).toLocaleDateString('fr-DZ', { day: '2-digit', month: 'long', year: 'numeric' })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Journal des actions */}
                                    {Array.isArray(sinistre.audit_history) && sinistre.audit_history.length > 0 && (
                                        <div>
                                            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.5px', marginBottom: '8px' }}>
                                                Journal des actions
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                {sinistre.audit_history.map((entry, idx) => {
                                                    const actionColors = {
                                                        'CREATION': { bg: '#f0fdf4', border: '#86efac', color: '#166534', icon: '🟢' },
                                                        'MODIFICATION': { bg: '#eff6ff', border: '#93c5fd', color: '#1d4ed8', icon: '🔵' },
                                                        'SUPPRESSION': { bg: '#fef2f2', border: '#fca5a5', color: '#991b1b', icon: '🔴' },
                                                        'UPLOAD_PHOTO': { bg: '#fdf4ff', border: '#d8b4fe', color: '#6b21a8', icon: '🟣' },
                                                    };
                                                    const ac = actionColors[entry.action] || { bg: 'var(--surface-2)', border: 'var(--border)', color: 'var(--text-secondary)', icon: '⚪' };
                                                    const who = entry.username || (entry.source === 'android_app' ? '📱 App' : 'Système');
                                                    return (
                                                        <div key={idx} style={{
                                                            padding: '7px 10px',
                                                            background: ac.bg,
                                                            border: `1px solid ${ac.border}`,
                                                            borderRadius: 'var(--radius-sm)',
                                                            fontSize: '11px'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <span>{ac.icon}</span>
                                                                <span style={{ fontWeight: '700', color: ac.color }}>{entry.action.replace('_', ' ')}</span>
                                                                <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                                    <Clock size={9} />
                                                                    {new Date(entry.created_at).toLocaleString('fr-DZ', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            </div>
                                                            <div style={{ color: 'var(--text-secondary)', marginTop: '2px', paddingLeft: '20px' }}>
                                                                par <strong>{who}</strong>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default SinistreDetail;

